import { useEffect, useState } from "react";
import { message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";

import "./../SetPassword/stylesSetPassword.css";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../utils/ErrorUtils";
import { LanguageSelect } from "../shared";
import PasswordPolicyChecklist from "../shared/PasswordPolicyChecklist/PasswordPolicyChecklist";
import { usePasswordPolicyValidation } from "../../hooks/usePasswordPolicyValidation";
import {
  getStoredGuestLanguage,
  setStoredGuestLanguage,
} from "../../utils/i18n/languagePreference";
import { applyI18nLanguage } from "../../utils/i18n/platformLanguage";
import {
  DEFAULT_LANGUAGE,
  canonicalizeLanguageTag,
} from "../../utils/i18n/supportedLanguages";
import {
  AUTH_RESPONSE_TYPES,
  classifyAuthResponse,
} from "../../utils/auth/authFlow";
import {
  REGULATOR_PORTAL_DASHBOARD_ROUTE,
  REGULATOR_PORTAL_SIGN_IN_ROUTE,
} from "./regulatorPortalRoutes";
import { setRegulatorPassword } from "../../queries/regulator-portal/regulatorAuthClient";
import {
  clearRegulatorPortalSession,
  setRegulatorPortalToken,
} from "../../utils/tokenUtils";
import {
  clearSetPasswordChallenge,
  readSetPasswordChallenge,
  writeSetPasswordChallenge,
} from "./regulatorSetPasswordChallenge";

const clearOtherSessions = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("email");
  localStorage.removeItem("lastLogInTime");
  localStorage.removeItem("affiliate_portal_token");
  localStorage.removeItem("affiliate_portal_lastLogInTime");
};

const resolveChallenge = (locationState) => {
  const fromRoute = locationState || {};
  if (fromRoute.username && fromRoute.sessionToken) {
    return {
      username: fromRoute.username,
      sessionToken: fromRoute.sessionToken,
    };
  }
  return readSetPasswordChallenge();
};

const RegulatorPortalSetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const challenge = resolveChallenge(location.state);
  const username = challenge?.username;
  const sessionToken = challenge?.sessionToken;
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInlinePolicyError, setShowInlinePolicyError] = useState(false);
  const [, setLanguageTick] = useState(0);

  const activeLanguage =
    canonicalizeLanguageTag(getStoredGuestLanguage()) ?? DEFAULT_LANGUAGE;

  const handleLanguageChange = async (lng) => {
    if (!lng || lng === activeLanguage) return;
    setStoredGuestLanguage(lng);
    await applyI18nLanguage(lng);
    setLanguageTick((tick) => tick + 1);
  };

  useEffect(() => {
    if (username && sessionToken) {
      writeSetPasswordChallenge({ username, sessionToken });
      return;
    }
    message.warning(AppStrings.PAM_SET_PASSWORD_SIGN_IN_FIRST);
    navigate(REGULATOR_PORTAL_SIGN_IN_ROUTE, { replace: true });
  }, [navigate, sessionToken, username]);

  const passwordPolicy = usePasswordPolicyValidation({
    email: username,
    password,
    audience: "regulator",
    enabled: Boolean(username && sessionToken),
  });
  const hasChallengeContext = Boolean(username && sessionToken);
  const isButtonDisabled =
    isSubmitting ||
    !passwordPolicy.allPassed ||
    confirmPassword !== password ||
    !hasChallengeContext;

  const leaveForSignIn = () => {
    clearSetPasswordChallenge();
    navigate(REGULATOR_PORTAL_SIGN_IN_ROUTE, { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!hasChallengeContext) return;
    if (!passwordPolicy.allPassed) {
      message.error(AppStrings.PAM_PASSWORD_REQUIREMENTS_NOT_MET);
      return;
    }
    setIsSubmitting(true);

    try {
      const data = await setRegulatorPassword({
        user_name: username,
        session: sessionToken,
        new_password: password,
      });
      const result = classifyAuthResponse(data);
      if (result.type === AUTH_RESPONSE_TYPES.FINAL_TOKENS) {
        clearSetPasswordChallenge();
        clearOtherSessions();
        clearRegulatorPortalSession();
        setRegulatorPortalToken(result.token);
        navigate(REGULATOR_PORTAL_DASHBOARD_ROUTE, { replace: true });
      } else {
        leaveForSignIn();
      }
    } catch (error) {
      const data = error?.response?.data;
      if (
        data?.error?.code === "invalid_password" &&
        passwordPolicy.applyPolicyResult(data?.error?.details)
      ) {
        setShowInlinePolicyError(true);
        return;
      }
      message.error(
        getApiErrorMessage(error, AppStrings.PAM_SET_PASSWORD_FAILED),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasChallengeContext) return null;

  return (
    <div className="set-password-container">
      <form
        className="set-password-card"
        onSubmit={handleSubmit}
        data-testid="regulator-set-password-form"
      >
        <div className="set-password-card-header">
          <div data-testid="regulator-portal-set-password-language">
            <LanguageSelect
              value={activeLanguage}
              onChange={handleLanguageChange}
            />
          </div>
          <h1>{AppStrings.PAM_SET_PASSWORD_TITLE}</h1>
          <p>{AppStrings.PAM_SET_PASSWORD_SUBTITLE}</p>
        </div>
        <div className="form-group">
          <label htmlFor="regulator-new-password">{AppStrings.PASSWORD}</label>
          <input
            id="regulator-new-password"
            type="password"
            value={password}
            onChange={(event) => {
              setPassword(event.target.value);
              setShowInlinePolicyError(false);
            }}
            placeholder={AppStrings.PAM_SET_PASSWORD_PLACEHOLDER}
          />
          <PasswordPolicyChecklist
            result={passwordPolicy.policyResult}
            isCurrent={passwordPolicy.isCurrent}
            isPending={passwordPolicy.isPending}
            hasError={passwordPolicy.hasError}
            showInlineError={showInlinePolicyError}
          />
        </div>
        <div className="form-group">
          <label htmlFor="regulator-confirm-password">
            {AppStrings.PAM_SET_PASSWORD_CONFIRM_LABEL}
          </label>
          <input
            id="regulator-confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            placeholder={AppStrings.PAM_SET_PASSWORD_CONFIRM_PLACEHOLDER}
          />
        </div>
        <button
          type="submit"
          disabled={isButtonDisabled}
          className={`set-password-button ${isButtonDisabled ? "disabled" : ""}`}
        >
          {isSubmitting ? AppStrings.SAVING : AppStrings.SAVE}
        </button>
      </form>
    </div>
  );
};

export default RegulatorPortalSetPassword;
